/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { IPearlLibrary } from '../Login/userSlice';
import {
  SearchDocumentVersionType,
  PearlApiDocumentVersionSearchType,
  PearlApiDocumentVersionType,
} from '../chat/chatSlice';
import { PageVignettesJsonType } from './vignettes_types';
import { FhirTypeType } from '../Fhir/fhir_utils';

export interface PersonType {
  id: string;
  name: string;
}

export interface DocumentType {
  id: string;
}

export enum DocumentVersionState {
  DRAFT = 'DRAFT',
  REVIEW = 'REVIEW',
  PUBLISHED = 'PUBLISHED',
  ARCHIVED = 'ARCHIVED',
  SNAPSHOT = 'SNAPSHOT',
  COLLABORATION = 'COLLABORATION',
  CONTEXTUALIZATION = 'CONTEXTUALIZATION',
  ON_HOLD = 'ON_HOLD',
  INTAKE = 'INTAKE',
  OTHER = 'OTHER',
}

export interface OrganizationType {
  id: string;
  topics: { topic: string; subtopic: string }[];
  personas: { id: string; name: string }[];
  careCategories: { id: string; name: string }[];
  patientGroups: { id: string; name: string }[];
  governanceModels: { id: string; name: string }[];
  docCategories: { id: string; name: string }[];
  users: {
    [id: string]: { id: string; name: string; title: string };
  };
  specialties: string[];
}

/** When searching, we search in a particular audience, or 'null' to mean any audience */
export type AudienceType = {
  persona?: string | null;
  careCategory?: string | null;
  patientGroup?: string | null;
};

/** A document can exist in multiple audiences */
export type AudiencesType = {
  /// care_category_ids mapped to the organization's care_category names
  care_categories: string[];
  /// patient_groups_ids mapped to the organization's patient_group names
  patient_groups: string[];
  /// persona_ids mapped to the organization's persona names
  personas: string[];
};

export type RoleType = 'OWNER' | 'CONTRIBUTOR' | 'REVIEWER' | 'FACILITATOR';

// What we get from the /v1/organization/../users endpoint
export type UserType = {
  id: string;
  email: string;
};

export type UserRoleType = {
  id?: string;
  email?: string; // either id and name are set, or email is set
  role: RoleType;
};

export type UserRoleFullType = {
  id?: string;
  name?: string;
  email?: string; // either id and name are set, or email is set
  role: RoleType;
};

export type UserRoleFullWithEmptyType = {
  id?: string;
  name?: string;
  email?: string; // either id and name are set, or email is set
  role: RoleType | undefined;
};

export interface DocumentVersionLockType {
  documentVersionId: string | undefined;
  start: string;
  userId: string;
  userEmail: string;
  userName: string;
}

export interface DocumentMetadataType {
  triageBoost?: boolean;
  objectID?: string;
  queryID?: string;
  highlight?: DocumentMetadataHighlightType;
}
export interface DocumentMetadataHighlightType {
  content?: {
    value?: string;
    has_highlight: boolean;
  };
  title?: {
    value?: string;
    has_highlight: boolean;
  };
  tags?: {
    value?: string[];
    has_highlight: boolean;
  };
  doc_categories?: {
    value?: string[];
    has_highlight: boolean;
  };
}

export type DocVersionType = 'DOCUMENT' | 'TRIAGE' | 'EMBEDDED';

/** If you're modifying this, you probably also want to modify
 *    convertPearlApiDocumentVersionToDocumentVersion()
 *  and
 *    convertDocumentVersionToPearlApiDocumentVersion()
 *  and
 *    PearlApiDocumentVersionType
 *
 *  @see updateDocumentVersionAudiences which adds the autogenerated fields
 */
export interface DocumentVersionWithoutGeneratedFieldsType {
  // A unique identifier for the document version.
  // Example: "9a16ad09-d06c-41d9-b8de-69af600fe75d"
  id: string;
  // A string representing the current state of the document version.
  // For example: DRAFT, REVIEW, PUBLISHED, ARCHIVED or SNAPSHOT
  state: DocumentVersionState;
  // There can be multiple document versions for a document. This represents the document associated with this document version.
  document: {
    // Identifier for the document.
    // Example: "ad2b5b17-94b1-4b52-a32a-827ffba27298"
    id: string;

    // Represents the organization associated with the document.
    // Example: { id: "be3d0552-47e8-403b-9783-4d2d3e1419c5" }  (Which is Providence)
    // organization: { id: string };

    // A string that is either DOCUMENT, TRIAGE or EMBEDDED.  The difference betweeen
    // DOCUMENT and TRIAGE is that TRIAGE documents don't have any guides, and have a boosting
    // when searching, so that we show triages first.
    // The idea here is that if you search, for example, for 'headache' then we want to show the
    // general headache triage, which can then direct you to the right guide based on your answers.
    // For EMBEDDED, these documents are ones that aren't ever shown themselves to the users,
    // but their pages can be embedded in other documents.  For example, a snippet of text that is used in
    // multiple documents. These get embedded with the html tag:
    // <embeddocumentpage data-doc="${doc_name}" data-doc-id="${doc_id}" data-page="${page_name}" data-slug="${doc_slug}" />`
    docType: DocVersionType;

    // A list of CONTRIBUTORs, REVIEWERs, and FACILITATORs associated with this document version.
    // {
    //   id?: string;
    //   name?: string;
    //   email?: string; // either id and name are set, or email is set
    //   role: RoleType;
    // }
    // Having one of these roles gives you extra permissions for this document.
    // (Note that the document owner is not included in this list)
    // Example: [{ id: "userID12345", name: "John Doe", email: "jdoe@example.com", role: "CONTRIBUTOR" }]
    members: UserRoleFullType[];

    // Represents the owner of the document.
    // Example: { id: "userID12345", name: "John Doe" }
    owner: { id: string; name: string };
    // contains the library id to which this document belongs to.
    library: IPearlLibrary;
    slug?: string; // Unique identifier for the document.
    doc_group?: { id: string; name: string };
  };
  assigneeId?: string | null; // The user ID of the hot potato assignee. We have to convert that to assigneeEmail
  // The title of the document version.
  // Example: "Document Title"
  title: string;
  // This only applies if the document state is "SNAPSHOT", which is a readonly copy of the document
  // intended to like a named previous-version.  This field is a brief description - for example:
  //  "First draft done, contextualization not yet started"
  snapshotName?: string;
  // This only applies if the document state is "SNAPSHOT". This is the id of the document version
  // that we took a snapshot of. Note that this is not the id of the document, but the id of the
  // document version.
  createdFromId?: string;
  // A list of tags associated with the document version. These are used for searching.
  // Example: ["heart", "cardiology", "chest pain", "heart pain"]
  tags: string[];
  // Displays an emoji next to the document. For example ["contextualized"] will display
  // a lightning bolt emoji next to the document: ⚡️
  flags?: string[];
  // Represents categories the document belongs to.
  // Example: [{   "id": "d0925a7a-896e-486d-8447-a636900c004c", "name": "Otolaryngology"}]
  docCategories: { id: string; name: string }[];
  // This is intended to mark the UMLS code for the document, but we don't make much use of this currently.
  // Example: { topic: "Cardiology", subtopic: "Heart Failure" }
  topics: { topic: string; subtopic: string }[];
  // Represents the credits or metadata for the document. id can be ignored and is used for sorting.
  // for example:
  // [
  //   { title: "Next maintenance", name: "2024-01-05T06:48:00.872Z", id: "0"},
  //   { title: "Published", name: "2023-10-19T10:17:41.782Z", id: "1"},
  //   { title: "Reviewer", name: "Joe Doe", id: "2" },
  // ],
  docCredits: { id: string; title: string; name: string }[];
  // Governance model associated with the document.
  // One of:
  //  Knowledge Sharing, Clinical Expertise, Best Practice, Clinical Pathway
  // Example:
  //  { id: "ad4a8bbf-ce42-4086-9ade-48756b3028ad", name: "Knowledge Sharing" }
  governanceModel?: { id: string; name: string };
  // Serialized date when the document was created.
  // Example: "2023-07-02T17:15:19.994Z"
  createdAt: string;
  firstPublishedAt?: string;
  // Serialized date when the document was last modified. But note this isn't currently
  // changed when a page is changed.
  // Example: "2023-07-02T17:15:20.994Z"
  lastModified: string;
  // List of people who reviewed the document.
  reviewers?: PersonType[];
  // Serialized date when the document was last published.
  // Example: "2023-07-02T17:30:19.994Z"
  published?: string;
  // Represents pages within the document version.
  pages: { [id: string]: PageType };

  // Represents the target audience for the document.
  // { care_categories: string[], patient_groups: string[], personas: string[] }
  audiences: AudiencesType;

  // ----------------------------------------------------------------
  // Following are generated:
  // ----------------------------------------------------------------

  // Indicates if the current user liked the document.
  // Example: true
  likedByYou?: boolean;
  // Indicates if the current user bookmarked the document.
  // Example: false
  bookmarkedByYou?: boolean;
  // Number of likes the document received.
  // Example: 100
  likesCount?: number;

  modifiedOnLoad?: string[]; // Set if document was modified on load and would like to be saved, and gives the reason why
  modifiedPageIdsOnLoad?: string[]; // Page ids that were rewritten on load and should be resaved with the next save
  library: IPearlLibrary;
  metadata?: DocumentMetadataType;
}
export interface DocumentVersionType extends DocumentVersionWithoutGeneratedFieldsType {
  // Generated fields
  // Note these are all calculated after loading the document version
  guidePageIds: string[];
  algorithmRootPageId?: string;
  pageIds: string[];
  nameToPageId: { [title: string]: string };
  assignee?: UserType;
}

export interface DocumentVersionSearchType extends PearlApiDocumentVersionSearchType {
  audiences: AudiencesType;
}

export interface BlobStorageFile {
  filename: string;
}

export interface MediaLibraryType {
  id: string;
  document_version_id: string;
  files: BlobStorageFile[] /* Read only */;
}

/** We get Observations (e.g. potassium level) for a patient.
 *
 *  This is what gets saved in the predefinedWidgets file.
 *
 *  Note there's no separate observation group file - if two FhirOurObservations have the same observationGroupId
 *  then they are grouped together - there's no further grouping.
 */
export interface FhirOurObservations {
  id: string; // a random id, just for sorting
  code: string;
  name: string;
  observationGroupId?: string; // The observation group id of this observation - to group multiple observations together as basically equivalent
}

/** @see simplifyObservationName
 *
 *  Note, if you change this, you must also add to @see FhirNetworkPredefinedWidget for them
 *  to be saved in the network json.
 *
 */
export type FhirRuleType = {
  type: 'DiagnosticReportName';
  name: string; // Pipe-separated CPT codes, LOINC codes, or display names. Values are normalized before comparison.
  muscName?: string; // Additional pipe-separated codes or display names for MUSC only.
  hideAllIfNoWhitelistMatches?: string; // e.g. 82570|82043 - i.e. a list of CPT codes
  loincCodes?: string; // e.g. 1234-5|6789-0 - a list of panel loinc codes
};

export type FhirCalculationType = string;

export const fhirTypes = [
  {
    id: 'Labs',
    value: 'Labs',
    bgColor: '#FFFFFF',
    legendColor: '#C3DDFD',
    borderColor: '#C3DDFD',
    fontColor: '#2753BF',
    darkenedBorderColor: '#4273b5',
  },
  {
    id: 'Medication',
    value: 'Medication',
    bgColor: '#9AD7E9',
    legendColor: '#9AD7E9',
    borderColor: '#9AD7E9',
    fontColor: '#25525F',
    darkenedBorderColor: '#7ab9d3',
  },
  {
    id: 'Procedure',
    value: 'Procedure',
    bgColor: '#F8CFDB',
    legendColor: '#F8CFDB',
    borderColor: '#F8CFDB',
    fontColor: '#67095C',
    darkenedBorderColor: '#c6a3af',
  },
  {
    id: 'Diagnostics',
    value: 'Diagnostics',
    bgColor: '#D3AED4',
    legendColor: '#D3AED4',
    borderColor: '#D3AED4',
    fontColor: '#744577',
    darkenedBorderColor: '#a88baa',
  },
  {
    id: 'Referral',
    value: 'Referral',
    bgColor: '#D4EAB0',
    legendColor: '#D4EAB0',
    borderColor: '#D4EAB0',
    fontColor: '#41571D',
    darkenedBorderColor: '#a8b48e',
  },
  {
    id: 'Not Set',
    value: undefined,
    bgColor: '#FFFFFF',
    legendColor: '#C3CDCC',
    borderColor: '#C3CDCC',
    fontColor: '#2753BF',
    darkenedBorderColor: '#424f4e',
  },
];

export const fhirPatientDataTypes = [
  {
    id: 'Medication',
    value: 'Medication',
  },
  {
    id: 'Vitals',
    value: 'Vitals',
  },
  {
    id: 'Problem List',
    value: 'Problem List',
  },
  {
    id: 'Medical History',
    value: 'Past Medical History',
  },
];

export const getDarkColorForFhirType = (type: string | null | undefined) =>
  fhirTypes.find((ft) => ft.id === type)?.darkenedBorderColor || 'black';
export const getColorForFhirType = (type: string | null | undefined) =>
  fhirTypes.find((ft) => ft.id === type)?.borderColor || 'black';

export const fhirTypesMap = new Map(fhirTypes.map((x) => [x.id, x]));

/**
 * This is the configured information about a FHIR widget. For example there is a "CMP" predefined widget
 * that can be associated with any <fhir> tag that wants to be pointed to a Comprehensive Metabolic Panel.
 * This is what gets saved in the predefinedWidgets file.
 *
 *  Note, if you change this, you must also add to @see FhirNetworkPredefinedWidget for them
 *  to be saved in the network json.
 */
export type FhirWidgetBase = {
  rules: FhirRuleType;
  msgWhenNoMatches?: string;
  title?: string /* If not set, will use the panel title. Deprecated and no longer wanted */;
  titleNote?: string /* If set, will show this in parens after the title */;
  msgDescription?: string /* Message to always show - e.g. Useful indicators for hydration */;
  showOnlyNewest?: boolean;
  showGFRDisclaimer?: boolean /* Show a caveat about GFR no longer being race related */;
  type?: FhirTypeType /* e.g. 'Labs' | 'Medication' | 'Procedure' | 'Diagnostics' | 'Referral' */;
  searchTerms?: string /* Historically this was called 'privateNotes' */;
  manualSearchTerms?: string /* Extra search terms for manual contextualization only */;
  negativeSearchTerms?: string /* Phrases that suppress contextualization matches */;
  actualPrivateNotes?: string /* Notes for creators (not shown to end user) */;
  lookbackTimeMonths?: number; // Default is 12 months.  Don't show labs older than this. -1 for no limit (aka lifetime)
  whitelist?: FhirOurObservations[] /* If set, only show these codes */;
  blacklist?: FhirOurObservations[] /* If set, hide these codes */;
  calculations?: FhirCalculationType[];
};

/**
 * Information about a particular <fhir id="foo"> tag or particular contextualized algorithm button.
 *
 * We assign every fhir tag to a predefined widget, so we no longer inherit from FhirWidgetBase
 * and only use the predefinedWidget field.
 * @todo Remove the isPredefinedWidget and just set the predefinedWidget to undefined if it's not a predefined widget.
 *
 * Note, these are saved in the page json, so don't change the names without
 * fixing in @see fixupPageFhir in fixupDocumentAndPages.ts
 *
 * Note this has changed over time:
 *  - 2025-09-3: Remove isPredefinedWidget - we always use a predefined widget now (or it's undefined)
 *  - 2025-09-3: change predefinedWidget to be predefinedWidgets (array) to allow multiple widgets
 */
export type FhirPageWidgetType = {
  id: string; // The fhirWidgetId in a <fhir id=""> tag or button. The id is random and created by the CKEditor when it creates a <fhir> tag, or fhir id of a contextualized button
  predefinedWidgets: {
    id: string; // The predefined widget id. Matches the id in FhirPredefinedWidget
    observationGroup?: {
      // This matches the observationGroupId in FhirOurObservations
      id: string; // The observation group id of the observations to highlight
    };
  }[];
};

/** Particular predefined widget. The id here matches the predefinedWidget field in the FhirPageWidgetType.
 *
 * Note, if you change this, you must also add to FhirNetworkPredefinedWidget for them to be saved in the network json.
 */
export type FhirPredefinedWidget = FhirWidgetBase & {
  id: string;
  name: string; // Name to show in the drop down list in creator
  wip?: boolean /* Work in progress.  If set, hide this widget from normal users */;
  deleted?: boolean /* If set, the widget is deprecated and hidden from the creator dropdown. Can be restored in the admin page */;
  replacementWidgetId?: string /* Widget to use when loading pages that reference this deprecated widget */;
  subsetOfWidgetIds?: string[] /* Derived on load. Other widgets whose rules.name contains this widget's rules.name */;
  almostSubsetOfWidgets?: {
    id: string;
    matchPercent: number;
  }[] /* Derived on load. Other widgets whose rules.name contains most of this widget's rules.name */;
};

/**
 * This is information about a <fhir> tag in the html, or a contextualized button.
 * It contains the y-position of the tag or button, so that we can draw a FhirCallout line from/to it.
 * Note that it belongs to a particular message (pageId, messageIndex),
 * and we also store the text inside the <fhir> tag or button, for the creator to get
 * autosuggestions on which widget this should be assigned to.
 *
 * The function @see useUpdateFhirSidebar is responsible for querying the dom to get these, and dispatch them to the fhir state.
 * And @see useUpdateButtonFhirSidebar for the button version.
 *
 * This is created on the fly and not stored in the json
 */
export type HtmlFhirInstance = {
  id: string; // The id in a <fhir id=""> tag. The id is random and created by the CKEditor when it creates a <fhir> tag, or fhir id of a contextualized button. Mathes the id in corresponding FhirPageWidgetType
  y: number;
  pageId: string;
  messageIndex: number;
  contextualized_text: string;
  htmlDataPredefinedFhirWidgetIds?: string[]; // Optional fallback when predefined widget ids come from the html node rather than page.fhir.idsToFhir
  inEditor?: boolean;
  embedded_doc_id?: string;
  isButton?: boolean;
  documentVersionId?: string;
};

/**
 * We store the HtmlFhirInstance in the state, but to be useful anywhere we need to look up the `id` in the page information
 * and get the widget information.
 *
 * This is created on the fly and not stored in the json
 */
export type HtmlFhirInstanceUngrouped = HtmlFhirInstance & {
  type: FhirTypeType; // Enum, e.g. "Labs" | "Medication" | "Procedure" | "Diagnostics" | "Referral"
  predefinedWidgetId: string | undefined; // This is the ID of the predefined widget. Matches the id in FhirPredefinedWidget
  predefinedWidget: FhirPredefinedWidget | undefined; // This is the predefined widget information
  observationGroupId: string | undefined;
};

/**
 * We store the HtmlFhirInstance in the state, but to be useful anywhere we need to look up the `id` in the page information
 * and get the widget information.  We also group multiple HtmlFhirInstance together if they are the same predefinedWidgetId
 * and are on the same messageIndex.
 *
 * @see mapHtmlFhirInstanceToHtmlFhirInstanceGrouped which groups the HtmlFhirInstance together and adds the predefinedWidgetId from the page information.
 *
 * @TODO We using this in lots of places where we should be using HtmlFhirInstance or HtmlFhirInstanceUngrouped.  Maybe use Omit<HtmlFhirInstance, 'id'> ?
 *
 * This is created on the fly and not stored in the json
 */
export type HtmlFhirInstanceGrouped = Omit<HtmlFhirInstanceUngrouped, 'observationGroupId' | 'isButton' | 'id'> & {
  observationGroupIds: string[];
  fhirWidgetIds: string[]; // The ids of the HtmlFhirInstance's that are grouped together. Matches the id in FhirPageWidgetType and HtmlFhirInstance
};

/** A variable that can be used in calculations.
 * E.g:
 *   name: Sodium
 *   observations: [{ code: '2847-3', name: 'Sodium', unit: 'mmol/L' }]
 */
export type FhirVariableType = {
  name: string;
  observations: [{ code: string; name: string; unit?: string }];
};

/** Variables used in calculations - map variable name to a list of pearlCodes */
export type FhirVariablesType = {
  [name: string]: FhirVariableType[];
};

export interface PageFhirType {
  idsToFhir: { [id: string]: FhirPageWidgetType };
}

/**
 *  Note if you want to add to this, you probably also want to add to @see PearlApiDocumentVersionType or similar
 */
interface PageBaseType {
  isRoot?: boolean; // True for root algorithm page, or guide page
  id: string;
  document_version_id: string;
  name: string;
  content?: string;
  tooltip?: string;
  fhir?: PageFhirType;
  isContextualized?: boolean;
  hasTrackedChanges?: boolean; // Whether this page has any unresolved ckeditor tracked changes on it. We don't want to publish with these
  meta?: PearlApiDocumentVersionType['pages'][0]['meta'];
  trusanaValue?: string | null;
}

export enum PageTypeEnum {
  GUIDE = 'guide',
  SWIMLANE = 'swimlane',
  TABS = 'tabs',
  MULTIPLE_QUESTIONS = 'multiple_questions',
  CALCULATOR = 'calculator',
  IF = 'if',
  JSON = 'json',
}

/** A page that has no options.
 *  It might include another page, but this is rare.  Has no questions or anything. */
export interface PageGuideType extends PageBaseType {
  pageType: PageTypeEnum.GUIDE;
  order: number; // A number to sort a list of guides by
  isRoot: true;
}

export interface LaneType {
  id: string;
  choices: { [key: string]: string }; // The key and value - both will be random IDs.
  link?: string; // e.g. "Page" or "#Some Doc"
  link_slug?: string; // e.g. some_doc - these are used to update link when the document is loaded, in fixupDocumentAndPages fixupPageNextLinks()
  content?: string;
  mergeLeft?: boolean;
}

/** A question with a list of multiple options.  You can select any combination, then all
 *  matches will run simultaneously (FIXME: TBD)
 */
export interface PageSwimLaneType extends PageBaseType {
  pageType: PageTypeEnum.SWIMLANE;
  lanes?: LaneType[];
  next_link?: string; // E.g. "Some Page" or "#Some Doc"
  next_link_slug?: string; // E.g. some_doc - these are used to update next_link when the document is loaded, in fixupDocumentAndPages fixupPageNextLinks()
  fallback_link?: string; // E.g. "Some Page" or "#Some Doc"
  fallback_link_slug?: string; // e.g. some_doc  if fallback_link is "#Some Doc", set by fixupDocumentAndPages fixupPageNextLinks()
  hide_summary?: boolean;
}

/** All the options are shown inside this page, in tabs */
export interface PageTabsType extends PageBaseType {
  content?: undefined /* Cannot have content itself. */;
  pageType: PageTypeEnum.TABS;
  options_exclusive_key?: string;
  options: PageOptionExclusiveType[];
}

export type QuestionType =
  | PageQuestionExclusiveType
  | PageQuestionMultipleChoiceType
  | PageQuestionFreeTextType
  | PageQuestionNumericalEntryType
  | PageQuestionUploadPictureType;

export type ReturnToHereInfo = { text: string };
/** Multi-question with a list of normal answers.
 *  Once all questions have been answered, the next_link page is called.
 *  The answers will set key-value pairs.
 */
export interface PageMultipleQuestionsType extends PageBaseType {
  pageType: PageTypeEnum.MULTIPLE_QUESTIONS;
  questions?: QuestionType[];
  next_link?: string; // E.g. #Some Doc
  next_link_slug?: string; // E.g. some_doc - these are used to update next_link when the document is loaded, in fixupDocumentAndPages fixupPageNextLinks()
  returnToHere?: ReturnToHereInfo;
}

/** Used by the PageCalculatorType
 *  Each button can have a points value, and the points are summed up (or other formula).
 *  Then the bins are used to show a message.
 */
export interface FormulaBin {
  id: string;
  min: number | undefined;
  max: number | undefined;
  max_is_inclusive?: boolean; // Whether to include the max value itself in the bin
  name: string; // Only used to label this in creator - user doesn't see this
  message: string; // Currently not used
  link?: string; // E.g. #Some Doc
  link_slug?: string; // E.g. some_doc - these are used to update next_link when the document is loaded, in fixupDocumentAndPages fixupPageNextLinks()
}

/**
 *  Very similar to PageMultipleQuestionsType, but the question answers can have points assigned to them.
 */
export interface PageCalculatorType extends PageBaseType {
  pageType: PageTypeEnum.CALCULATOR;
  questions?: QuestionType[];
  next_link?: string; // E.g. #Some Doc
  next_link_slug?: string; // E.g. some_doc - these are used to update next_link when the document is loaded, in fixupDocumentAndPages fixupPageNextLinks()
  returnToHere?: { set: true; text: string };
  hideScores?: boolean; // Whether to hide the scores from the user
  hide_summary?: boolean; // Whether to hide the summary from the user

  // The formula to calculate the score.  Currently only 'sum' is supported, but the idea is to expand to objects
  formula?: { type: 'sum' } | { type: 'formula'; formula: string };
  formulaBins?: FormulaBin[]; // The bins to show a message
}

/** A logical 'IF' page, that branches depending on the given value
 */
export interface PageIfType extends PageBaseType {
  pageType: PageTypeEnum.IF;
  variable?: string;
  true_link?: string; // e.g. #Some Doc
  true_link_slug?: string; // e.g. some_doc - these are used to update true_link when the document is loaded, in fixupDocumentAndPages fixupPageNextLinks()
  false_link?: string; // e.g. #Some Doc
  false_link_slug?: string; // e.g. some_doc - these are used to update false_link when the document is loaded, in fixupDocumentAndPages fixupPageNextLinks()
}

/** A page that is just a JSON object, used for Vignettes  - @see PageVignettesJsonType */
export interface PageJSONType extends PageBaseType {
  pageType: PageTypeEnum.JSON;
  schema: string;
  json: object;
}

export type PageType =
  | PageSwimLaneType
  | PageGuideType
  | PageMultipleQuestionsType
  | PageTabsType /* Only used internally */
  | PageIfType
  | PageCalculatorType
  | PageJSONType
  | PageVignettesJsonType;

// When dealing with page changes, don't allow fhir changes to be set, because it's ambgious whether to merge
// or replace the fhir object.
export type PartialPageType = Partial<PageType> & { fhir?: undefined };

type PageOptionBaseType = {
  id: string; // Used for drag-and-drop reordering
  fhirWidgetId?: string; // Used for fhir id, guaranteed to start with 'b'
  fhirObservationGroupId?: string; // Used for fhir observation group id, guaranteed to start with 'g'
  text: string;
  link?: string; // This can be like:  "Bar" to mean page called "Bar". Or "#Foo" to mean a link to a document called "Foo", or "#Foo#Bar" to mean a link to a document called "Foo" and its page called "Bar"
  linkSlug?: string; // When the link is like "#Foo", the linkSlug is set to the more robust slug name of the document, so that we can auto update the link if the document is renamed. Set in fixupDocumentAndPages updateButtonSlug()
  points?: number; // e.g. worth 1 point - for summing up the points etc for calculator
  tooltip?: string;
  summarytext?: string;
  negativesummarytext?: string;
  isNone?: boolean; // If this is a special 'none' option.  In single-choice this does nothing but make it blue
  showEMHighlight?: boolean; // If this option leads to a page with EM info. This is manually marked by creators during document creation.
  // value?: string /* This is the old naming. Use exclusive_value instead, and should be only equal to YES for multiple_choice */;
};

/** This is a button in a single-choice question. */
export interface PageOptionExclusiveType extends PageOptionBaseType {
  exclusive_value?: string;
}

/** This is a button in a multiple-choice question */
export interface PageOptionMultipleChoiceType extends PageOptionBaseType {
  // exclusive_key?: string /* This is the old naming. When reading from network, rename to multiple_choice_key. When saving, save this for backwards compatibility  */;
  multiple_choice_key?: string; // This used to be incorrectly called "exclusive_key"
}

export interface PageQuestionBaseType {
  id: string; // Used for drag-and-drop reordering
  type: PageQuestionTypeEnum;
  tooltip?: string;
  fhir?: FhirPageWidgetType;
  summarytextPrefix?: string;
  negativeSummarytextPrefix?: string; // e.g. "Patient denies having"
  questionHtml?: string; // The title as html. Will be converted to plain text for mobile
  question?: string; // The title as plain text, for backwards compatibility - When read from network, convert to questionHtml and when saving set this
}

export type ButtonStyle = 'Standard' | 'Image Buttons' | 'Dropdown List';

export interface PageQuestionExclusiveType extends PageQuestionBaseType {
  type: 'exclusive';
  exclusive_key?: string /* if not set, the question is used instead */;
  options: PageOptionExclusiveType[];
  buttonStyle?: ButtonStyle;
}

export interface PageQuestionMultipleChoiceType extends PageQuestionBaseType {
  type: 'multiple_choice';
  tooltip?: string;
  options: PageOptionMultipleChoiceType[];
  buttonStyle?: ButtonStyle;
}

export interface PageQuestionFreeTextType extends PageQuestionBaseType {
  type: 'free_text';
  required?: boolean;
}

export interface PageQuestionNumericalEntryType extends PageQuestionBaseType {
  type: 'numerical_entry';
  required?: boolean;
  min?: number;
  max?: number;
  step?: number;
}

export interface PageQuestionUploadPictureType extends PageQuestionBaseType {
  type: 'upload_picture';
  required?: boolean;
}

export type PageQuestionTypeEnum = 'exclusive' | 'multiple_choice' | 'free_text' | 'numerical_entry' | 'upload_picture';

/***************** END OF PAGE TYPE INFO ***************/

export type SelectedButtonInfo = {
  value: string;
  summarytext: { keyStr: string; valueStr: string }; // Note this is guaranteed to not be undefined.  If the button doesn't define, then we set it from text - see getSummaryText()
  points?: number; // How many points this answer is - for a calculator
  isNone?: boolean;
  messageIndex: number; // Which message it is in.  Used for ordering the summary output nicely
  questionIndex?: number; // Which question it is, in the page.  Used for ordering the summary output nicely
  optionIndex?: number; // Which button it is, inside the question.  Also used for ordering the summary output nicely
  hideFromSummary?: boolean; // Whether to show in the final output
  hideScore?: boolean; // Whether to show the score in the final output
};

export type LinkSlugType = {
  link: string; // E.g. "#Some Doc"
  link_slug: string | undefined; // E.g. some_doc
  linkDocName?: string; // E.g. "Some Doc" - from data-doc-name e.g <a data-doc-name="Some Doc" href="?slug.."/>
};

export enum MessageTypeEnum {
  NO_BUBBLE = 'NO_BUBBLE',
  NO_SEARCH_MATCH = 'NO_SEARCH_MATCH',
  MULTIPLE_MATCHES = 'MULTIPLE_MATCHES',
}
/** This is the message as its stored in a list of messages that the user actually sees.
   So these are the chat 'bubbles'
  */
export type MessageType = {
  index: number; // index in state.chat.messages.  -1 if not added yet
  content?: JSX.Element | string;
  no_bubble?: boolean;
  pages?: PageType[]; // Which pages are shown in this message
  pagesUsed?: string[]; // Which pages have been shown in this and all previous messages
  selected?: { [key: string]: SelectedButtonInfo };
} & (
  | {
      is_sent?: false;
      extra?:
        | {
            type: 'WRONG_AUDIENCE';
            title: string;
            publishedOnly: boolean;
            searchTerm: string;
            document_version_id: string;
            document_audiences: AudiencesType;
            slug: string | undefined;
          }
        | {
            type: 'MATCH_FOUND';
            document_version_id: string;
            was_partial_match: boolean;
            document_audiences: AudiencesType;
          }
        | {
            type: 'NO_SEARCH_MATCH';
            publishedOnly: boolean;
            searchTerm: string;
            howActivated: HowActivatedType;
            searchId: string /* Random id For analytics */;
          }
        | {
            type: 'MULTIPLE_MATCHES';
            publishedOnly: boolean;
            searchTerm: string;
            versions: DocumentVersionSearchType[];
            searchId: string /* Random id For analytics */;
            howActivated: HowActivatedType;
            totalVersions: number; // total number of docs found for current search
          }
        | {
            type: 'NO_PAGES';
          }
        | {
            type: 'WELCOME_MESSAGE';
          }
        | {
            type: 'ALGORITHM_PAGE';
            need_confirmation: boolean;
            have_confirmation: boolean;
            algorithm_error?: string; // If we have another error while running, show this error
            cant_confirm_error?: string; // If we can't confirm (e.g. no calculator bin matches, or no next_link), this is the error to show
            has_only_leaf_pages: boolean; // If the algorithm has only leaf pages, we show the summary
          };
    }
  | {
      is_sent?: true;
      hide_sent?: boolean; // Whether we show it - only relevent when is_sent is true. Used so we scroll to the right place
      extra?:
        | {
            type: 'START_PAGE' /* When we click on a page in algorithm page, and we want to show that page first */;
            document_name: string;
            page_name?: string; // Only set if not the root page
          }
        | {
            type: 'OPEN_LINK';
            external_link: LinkSlugType /* When we need to run an external link. e.g "Some Doc" (note: without the #) */;
          };
    }
);

/** This is what gets passed around in the chatSlice, to indicate that a user
   just typed or just clicked something.  The chat slice looks up the page that it is
   referring to, and turns this into a MessageType (chat bubble) that the user sees. */
export type ChatMessageType = {
  humanTyped?: boolean;
  text: string;
  document_version_id?: string;
  link?: LinkSlugType /* E.g. { link: "#Some Doc", link_slug: "some_doc" } */;
  category_value?: string;
  alternative_document_versions?: SearchDocumentVersionType;
};

export const messageOptionsTypeList = [
  { id: 'exclusive', value: 'Single Choice Question' },
  { id: 'multiple_choice', value: 'Multiple Choice Question' },
  { id: 'free_text', value: 'Free Text' },
  { id: 'numerical_entry', value: 'Numerical Entry' },
  { id: 'upload_picture', value: 'Upload picture' },
] as const;

export type DocumentVersionActionType = 'publish' | 'reject' | 'submit' | 'archive' | 'republish from archive';

/* userTyped means the user actually typed it, or clicked an option in the autocomplete list.
 * algorithmClick means the algorithm directed to that new search term
 * urlLink means the user clicked a search result link, or clicked a permalink from somewhere else,
 *      or the audience changed */
export type HowActivatedType =
  | 'userTyped'
  | 'algorithmClick'
  | 'urlLink'
  | 'teamsBot'
  | 'creatorAlgorithmClick'
  | 'startingPage'
  | 'aiMatch';

/** For grouping together CPT codes as equivalent for the purpose of widgets */
export type GroupedObservationType = {
  id: string;
  name: string;
  mergeWith?: string[]; // list of ids to merge with this one. We don't actually combine them to make it easier to undo, and to not break the existing links to an id
  cptCodes: Record<
    string /* code */,
    {
      name: string;
    }[]
  >;
};
